import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { User } from "oidc-client-ts";
import {
  AuthRecoveryRequiredError,
  BffClient,
  mergeSessionMessages,
  type SessionSummary,
} from "../api/client";
import { AppShell } from "../components/AppShell";

const maxMessageLength = 2_000;
const quickActions = [
  "Summarize last quarter",
  "Create invoice draft",
  "Schedule meeting",
];

type ChatProps = {
  user: User;
  onSignedOut: () => void;
};

type SessionRow = Pick<SessionSummary, "session_id"> & {
  title?: string | null;
  last_message_at?: string | null;
  last_message_preview?: string | null;
};

function claimString(user: User, claim: string): string | null {
  const value = user.profile[claim];
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }

  if (Array.isArray(value)) {
    const strings = value.filter((item): item is string => typeof item === "string" && !!item.trim());
    return strings.length ? strings.join(", ") : null;
  }

  return null;
}

function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);
    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    };
  }, []);

  return isOnline;
}

export function Chat({ user, onSignedOut }: ChatProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [lastDecision, setLastDecision] = useState<string | null>(null);
  const [streamState, setStreamState] = useState<"connected" | "reconnecting">("connected");
  const isOnline = useOnlineStatus();
  const client = useMemo(() => new BffClient(user), [user]);
  const activeTenantId = claimString(user, "tenant_id");

  const sessions = useQuery({
    queryKey: ["sessions"],
    queryFn: () => client.listSessions(),
  });
  const messages = useQuery({
    queryKey: ["messages", activeSessionId],
    queryFn: () => client.listMessages(activeSessionId ?? ""),
    enabled: !!activeSessionId,
  });
  const sendMessage = useMutation({
    mutationFn: async (content: string) => {
      if (activeSessionId) {
        return {
          sessionId: activeSessionId,
          response: await client.sendMessage(activeSessionId, content, "text_plain"),
        };
      }

      const created = await client.createSession({
        title: content.slice(0, 80),
        initial_message: {
          content,
          content_type: "text_plain",
        },
      });
      return {
        sessionId: created.session.session_id,
        response: created.initial_message ?? null,
        created,
      };
    },
    onMutate: () => setLastDecision(null),
    onSuccess: async (result) => {
      setDraft("");
      setActiveSessionId(result.sessionId);
      if (result.response) {
        setLastDecision(result.response.decision);
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["messages", result.sessionId] }),
        queryClient.invalidateQueries({ queryKey: ["sessions"] }),
      ]);
    },
  });
  const sessionRows: SessionRow[] = sessions.data ?? [];
  const messageRows = messages.data ?? [];
  const remainingCharacters = maxMessageLength - draft.length;
  const canSubmit = isOnline && !!draft.trim() && !sendMessage.isPending && remainingCharacters >= 0;
  const hasInitialLoading = sessions.isLoading || messages.isLoading;
  const hasError = sessions.isError || messages.isError;
  const isReconnecting =
    !isOnline ||
    streamState === "reconnecting" ||
    sessions.fetchStatus === "paused" ||
    messages.fetchStatus === "paused";

  useEffect(() => {
    if (sessionRows.length > 0 && !sessionRows.some((session) => session.session_id === activeSessionId)) {
      setActiveSessionId(sessionRows[0].session_id);
    }
  }, [activeSessionId, sessionRows]);

  useEffect(() => {
    if (!activeSessionId) {
      return;
    }
    setStreamState("connected");
    const connection = client.connectSessionEvents(activeSessionId, {
      onEvent: (event) => {
        setStreamState("connected");
        if (event.type !== "message") {
          return;
        }
        queryClient.setQueryData(["messages", activeSessionId], (current: unknown) =>
          mergeSessionMessages(Array.isArray(current) ? current : [], event.messages),
        );
        void queryClient.invalidateQueries({ queryKey: ["sessions"] });
      },
      onError: (error) => {
        if (error instanceof AuthRecoveryRequiredError) {
          onSignedOut();
          return;
        }
        setStreamState("reconnecting");
      },
    });
    const close = () => connection.close();
    window.addEventListener("pagehide", close, { once: true });
    return () => {
      window.removeEventListener("pagehide", close);
      connection.close();
    };
  }, [activeSessionId, client, onSignedOut, queryClient]);

  function submitDraft() {
    const content = draft.trim();
    if (content && canSubmit) {
      sendMessage.mutate(content);
    }
  }

  return (
    <AppShell user={user} activeTenantId={activeTenantId} onSignedOut={onSignedOut}>
      <section className="workspace">
        <aside className="session-list">
          <div className="panel-heading">
            <p className="eyebrow">Sessions</p>
            <h2>Agent chat</h2>
          </div>
          {sessions.isLoading ? <p className="muted">Loading sessions</p> : null}
          {sessions.isError ? <p className="error">Unable to load sessions</p> : null}
          {!sessions.isLoading && !sessions.isError && sessionRows.length === 0 ? (
            <div className="empty-state compact">
              <strong>No saved sessions yet</strong>
              <span>A server-created workspace opens when you send the first message.</span>
            </div>
          ) : null}
          <div className="session-stack">
            {sessionRows.map((session) => (
              <button
                type="button"
                className={`session-row ${session.session_id === activeSessionId ? "is-active" : ""}`}
                key={session.session_id}
                onClick={() => setActiveSessionId(session.session_id)}
              >
                <span>{session.title || session.session_id}</span>
                <small>{session.last_message_preview || (session.last_message_at ? "Recent activity" : "Ready")}</small>
              </button>
            ))}
          </div>
        </aside>
        <section className="chat-panel">
          <div className="chat-header">
            <div>
              <p className="eyebrow">Session</p>
              <h2>{activeSessionId ?? "New session"}</h2>
            </div>
            <div className={`connection-pill ${isReconnecting ? "is-warning" : ""}`}>
              {isReconnecting ? "Reconnecting" : hasError ? "Needs attention" : "Connected"}
            </div>
          </div>
          {isReconnecting ? (
            <div className="status-banner" role="status">
              Reconnecting. New messages are held until the browser is online again.
            </div>
          ) : null}
          <div className="message-list">
            {hasInitialLoading ? (
              <div className="message-skeleton" aria-label="Loading messages">
                <span />
                <span />
                <span />
              </div>
            ) : null}
            {messages.isError ? <p className="error">Unable to load messages.</p> : null}
            {!hasInitialLoading && !messages.isError && messageRows.length === 0 ? (
              <div className="empty-state">
                <strong>No messages in this session</strong>
                <span>Use a quick action or write a text/plain message to start the thread.</span>
              </div>
            ) : null}
            {messageRows.map((message) => (
              <article className={`message ${message.direction}`} key={message.message_id}>
                <header>
                  <strong>{message.direction === "inbound" ? "You" : "EvoBuddy"}</strong>
                  <span>{message.content_type.replace("_", "/")}</span>
                </header>
                <p>{message.content}</p>
              </article>
            ))}
          </div>
          <form
            className="composer"
            onSubmit={(event) => {
              event.preventDefault();
              submitDraft();
            }}
          >
            <div className="quick-actions" aria-label="Quick actions">
              {quickActions.map((action) => (
                <button type="button" key={action} onClick={() => setDraft(action)}>
                  {action}
                </button>
              ))}
            </div>
            <div className="composer-input">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                value={draft}
                onChange={(event) => setDraft(event.target.value.slice(0, maxMessageLength))}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    submitDraft();
                  }
                }}
                placeholder="Send a text/plain instruction to EvoBuddy..."
                rows={4}
                maxLength={maxMessageLength}
              />
            </div>
            <div className="composer-footer">
              <div>
                <span className="content-type">text/plain</span>
                <span className={remainingCharacters < 160 ? "limit is-low" : "limit"}>
                  {remainingCharacters} characters left
                </span>
                {lastDecision ? <span className="decision">Decision: {lastDecision}</span> : null}
                {sendMessage.isError ? <span className="error">Message was not sent.</span> : null}
              </div>
              <button type="submit" disabled={!canSubmit}>
                {sendMessage.isPending ? "Sending" : "Send"}
              </button>
            </div>
          </form>
        </section>
      </section>
    </AppShell>
  );
}
